import { ContentType, contentTypeFromJSON } from 'proto/generated/tintin_content_policy';
import { AssociatedContentType, EntityType, entityTypeFromJSON } from 'proto/generated/inspector_gadget';
import {
  isAlbumsReportingEnabledForCountry,
  isMusicVideosReportingEnabledForCountry,
  isPodcastReportingEnabledForCountry,
  isTracksReportingEnabledForCountry,
  isAuthorReportingEnabledForCountry,
} from 'components/ContentReporting/market-helper';
import { FEATURE_FLAG } from 'components/ContentReporting/feature-flags';
import { EntityType as CommonEntityType } from 'proto/generated/spotify/catalogmanagement/entity/v1/entity';
import { AssetType } from 'proto/generated/spotify/catalogmanagement/entity/v1/asset';

import {
  ASSET_EXISTENCE_CHECK_COMMON_ENTITIES_TO_ASSETS,
  ASSET_EXISTENCE_CHECK_ENTITIES_TO_ASSETS,
} from '../constants/asset';

export const associatedContentTypeFromContentType = (contentType: ContentType): AssociatedContentType => {
  switch (contentType) {
    case ContentType.CONTENT_TYPE_PLAYLIST_TITLE:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_PLAYLIST_TITLE;
    case ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_PLAYLIST_DESCRIPTION;
    case ContentType.CONTENT_TYPE_PLAYLIST_IMAGE:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_PLAYLIST_IMAGE;
    case ContentType.CONTENT_TYPE_USER_IMAGE:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_USER_IMAGE;
    case ContentType.CONTENT_TYPE_NOW_PLAYING:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_TRACK_CANVAS;
    case ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_ARTIST_MERCH;
    case ContentType.CONTENT_TYPE_MUSIC_VIDEO:
      return AssociatedContentType.ASSOCIATED_CONTENT_TYPE_TRACK_MUSIC_VIDEO;
    default:
      throw new Error(`Unsupported content type for Associated Content: ${contentType}`);
  }
};

export type ContentTypeAssetTypeMapping = { contentType: ContentType; commonAssetType: AssetType };
export type EntityTypesWithAssetTypes = Partial<Record<CommonEntityType, ContentTypeAssetTypeMapping[]>>;

export const getAvailableAssetTypesPerCountry = ({
  isEmployee,
  userCountryIsoCode,
  isFeatureFlagEnabled,
}: {
  isEmployee: boolean;
  userCountryIsoCode: string;
  // NOTE: Do not remove isFeatureFlagEnabled even if unused. This simplifies adding feature flags in the future.
  isFeatureFlagEnabled?: (featureFlag: FEATURE_FLAG) => boolean;
}): EntityTypesWithAssetTypes => {
  const isEmployeeOrPodcastReportingEnabledForMarket =
    isEmployee || isPodcastReportingEnabledForCountry(userCountryIsoCode);
  const entityTypeEpisodeContentTypes = isEmployeeOrPodcastReportingEnabledForMarket
    ? [
        { contentType: ContentType.CONTENT_TYPE_AUDIO, commonAssetType: AssetType.AUDIO },
        { contentType: ContentType.CONTENT_TYPE_ARTWORK, commonAssetType: AssetType.IMAGE },
        { contentType: ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
        { contentType: ContentType.CONTENT_TYPE_TITLE, commonAssetType: AssetType.TITLE },
      ]
    : [];

  return {
    [CommonEntityType.UNKNOWN_ENTITY_TYPE]: [],
    [CommonEntityType.AUDIOBOOK]: [
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_ARTWORK, commonAssetType: AssetType.IMAGE },
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_NAME, commonAssetType: AssetType.TITLE },
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
    ],
    [CommonEntityType.AUDIOBOOK_CHAPTER]: [
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_AUDIO, commonAssetType: AssetType.AUDIO },
      { contentType: ContentType.CONTENT_TYPE_AUDIOBOOK_CHAPTER_NAME, commonAssetType: AssetType.TITLE },
    ],
    [CommonEntityType.USER]: [
      { contentType: ContentType.CONTENT_TYPE_USER_IMAGE, commonAssetType: AssetType.PROFILE_IMAGE },
    ],
    [CommonEntityType.SHOW]: isEmployeeOrPodcastReportingEnabledForMarket
      ? [
          { contentType: ContentType.CONTENT_TYPE_ARTWORK, commonAssetType: AssetType.IMAGE },
          { contentType: ContentType.CONTENT_TYPE_PODCAST_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
          { contentType: ContentType.CONTENT_TYPE_TITLE, commonAssetType: AssetType.TITLE },
        ]
      : [],
    [CommonEntityType.EPISODE]: entityTypeEpisodeContentTypes,
    [CommonEntityType.ALBUM]:
      isEmployee || isAlbumsReportingEnabledForCountry(userCountryIsoCode)
        ? [{ contentType: ContentType.CONTENT_TYPE_ARTWORK, commonAssetType: AssetType.IMAGE }]
        : [],
    [CommonEntityType.TRACK]:
      isEmployee || isTracksReportingEnabledForCountry(userCountryIsoCode)
        ? [
            { contentType: ContentType.CONTENT_TYPE_AUDIO, commonAssetType: AssetType.AUDIO },
            { contentType: ContentType.CONTENT_TYPE_NOW_PLAYING, commonAssetType: AssetType.CANVAS_ASSET },
            { contentType: ContentType.CONTENT_TYPE_MUSIC_STORYLINES, commonAssetType: AssetType.STORYLINE },
            ...(isEmployee || isMusicVideosReportingEnabledForCountry(userCountryIsoCode)
              ? [{ contentType: ContentType.CONTENT_TYPE_MUSIC_VIDEO, commonAssetType: AssetType.VIDEO }]
              : []),
          ]
        : [
            { contentType: ContentType.CONTENT_TYPE_NOW_PLAYING, commonAssetType: AssetType.CANVAS_ASSET },
            ...(isMusicVideosReportingEnabledForCountry(userCountryIsoCode)
              ? [{ contentType: ContentType.CONTENT_TYPE_MUSIC_VIDEO, commonAssetType: AssetType.VIDEO }]
              : []),
          ],
    [CommonEntityType.PLAYLIST]: [
      { contentType: ContentType.CONTENT_TYPE_PLAYLIST_IMAGE, commonAssetType: AssetType.PLAYLIST_IMAGE },
      { contentType: ContentType.CONTENT_TYPE_PLAYLIST_TITLE, commonAssetType: AssetType.TITLE },
      { contentType: ContentType.CONTENT_TYPE_PLAYLIST_DESCRIPTION, commonAssetType: AssetType.DESCRIPTION },
    ],
    [CommonEntityType.ARTIST]: [
      { contentType: ContentType.CONTENT_TYPE_ARTIST_BIO_TEXT, commonAssetType: AssetType.ARTIST_BIOGRAPHY },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_PICK_TEXT, commonAssetType: AssetType.ARTIST_PICK },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_PROFILE_IMAGE, commonAssetType: AssetType.ARTIST_AVATAR },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_BANNER_IMAGE, commonAssetType: AssetType.ARTIST_HEADER },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_MERCHANDISE, commonAssetType: AssetType.ARTIST_MERCHANDISE },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_GALLERY_IMAGE, commonAssetType: AssetType.ARTIST_GALLERY_IMAGE },
    ],
    [CommonEntityType.ARTIST_EXPRESSION]: [
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE, commonAssetType: AssetType.TITLE },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO, commonAssetType: AssetType.VIDEO },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO, commonAssetType: AssetType.AUDIO },
    ],
    [CommonEntityType.ALBUM_PRERELEASE_EXPRESSION]: [
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_TITLE, commonAssetType: AssetType.TITLE },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_VIDEO, commonAssetType: AssetType.VIDEO },
      { contentType: ContentType.CONTENT_TYPE_ARTIST_VIDEO_AUDIO, commonAssetType: AssetType.AUDIO },
    ],
    [CommonEntityType.POLL]: [
      { contentType: ContentType.CONTENT_TYPE_POLL_QUESTION_TEXT, commonAssetType: AssetType.POLL_QUESTION_TEXT },
      { contentType: ContentType.CONTENT_TYPE_POLL_OPTIONS_TEXT, commonAssetType: AssetType.POLL_OPTIONS_TEXT },
    ],
    [CommonEntityType.COMMENT]: [
      { contentType: ContentType.CONTENT_TYPE_COMMENT_TEXT, commonAssetType: AssetType.COMMENT_TEXT },
    ],
    [CommonEntityType.QUESTION]: [], // deprecated
    [CommonEntityType.RESPONSE]: [], // deprecated
    [CommonEntityType.MERCH]: [
      { contentType: ContentType.CONTENT_TYPE_MERCH_TITLE, commonAssetType: AssetType.MERCH_TITLE },
      { contentType: ContentType.CONTENT_TYPE_MERCH_DESCRIPTION, commonAssetType: AssetType.MERCH_DESCRIPTION },
      { contentType: ContentType.CONTENT_TYPE_MERCH_IMAGE_GALLERY, commonAssetType: AssetType.MERCH_IMAGE_GALLERY },
    ],
    [CommonEntityType.CHAT_MESSAGE]: isFeatureFlagEnabled?.(FEATURE_FLAG.CHAT_MESSAGE)
      ? [{ contentType: ContentType.CONTENT_TYPE_CHAT_MESSAGE_TEXT, commonAssetType: AssetType.CHAT_MESSAGE_TEXT }]
      : [],
    [CommonEntityType.EPISODE_VIDEO_PREVIEW]: [
      {
        contentType: ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_TITLE,
        commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_TITLE,
      },
      {
        contentType: ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_AUDIO,
        commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_AUDIO,
      },
      {
        contentType: ContentType.CONTENT_TYPE_EPISODE_VIDEO_PREVIEW_VIDEO,
        commonAssetType: AssetType.EPISODE_VIDEO_PREVIEW_VIDEO,
      },
    ],
    [CommonEntityType.PODCAST_THUMBNAIL]: [], // deprecated
    [CommonEntityType.SUPPLEMENTARY_MATERIAL]: [
      { contentType: ContentType.CONTENT_TYPE_SUPPLEMENTARY_PDF, commonAssetType: AssetType.SUPPLEMENTARY_PDF },
    ],
    [CommonEntityType.AUDIOBOOK_AUTHOR]:
      isFeatureFlagEnabled?.(FEATURE_FLAG.AUTHOR) && isAuthorReportingEnabledForCountry(userCountryIsoCode)
        ? [
            { contentType: ContentType.CONTENT_TYPE_AUTHOR_NAME, commonAssetType: AssetType.NAME },
            { contentType: ContentType.CONTENT_TYPE_AUTHOR_IMAGE, commonAssetType: AssetType.IMAGE },
            { contentType: ContentType.CONTENT_TYPE_AUTHOR_BIOGRAPHY, commonAssetType: AssetType.BIOGRAPHY },
          ]
        : [],
  };
};

export const couldHaveCommonAssetExistenceCheck = (entityType: CommonEntityType, assetType: AssetType) => {
  return ASSET_EXISTENCE_CHECK_COMMON_ENTITIES_TO_ASSETS[entityType]?.includes(Number(AssetType[assetType]));
};

export const couldHaveAssetExistenceCheck = ({
  entityType,
  contentType,
}: {
  entityType?: EntityType;
  contentType?: ContentType;
}) => {
  const contentTypeEnum = !!contentType ? contentTypeFromJSON(contentType) : undefined;
  const entityTypeEnum = !!entityType ? entityTypeFromJSON(entityType) : undefined;

  if (!contentTypeEnum || !entityTypeEnum) {
    return false;
  }

  return ASSET_EXISTENCE_CHECK_ENTITIES_TO_ASSETS[entityTypeEnum]?.includes(contentTypeEnum);
};
